import React from 'react';
import {
  Drawer,
  DrawerContent,
  InputPhone,
  convertPhoneNumberStringToObject,
  convertPhoneNumberObjectToString,
} from '@yuhuinc/ui-library';
import { Space, Form, Input, Button, Divider, Select, message } from 'antd';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getTranslatedString } from 'helpers/i18n';
import { apiPutRequest } from 'helpers/ApiHelper';

const StyledSpace = styled(Space)`
  width: 100%;
  .ant-picker {
    width: 100%;
  }
`;

const translate = (key) =>
  getTranslatedString(
    `landlordPortal.applicationManagement.applicationOverview.review.references.referenceDrawer.${key}`
  );

const requiredField = {
  required: true,
  message: getTranslatedString('requiredField'),
};

const PHONE_TYPE_OPTIONS = [
  { label: 'mobile', value: 'mobile' },
  { label: 'home', value: 'home' },
  { label: 'business', value: 'business' },
  { label: 'other', value: 'other' },
];

const RELATIONSHIP_OPTIONS = [
  { label: 'family', value: 'family' },
  { label: 'friend', value: 'friend' },
  { label: 'coworker', value: 'coworker' },
  { label: 'formerLandlord', value: 'former_landlord' },
  { label: 'employer', value: 'employer' },
  { label: 'other', value: 'other' },
];

const REFERENCE_TYPE_OPTIONS = [
  { label: 'personal', value: 'personal' },
  { label: 'employer', value: 'employer' },
];

const DURATION_OPTIONS = [
  { label: '<1y', value: '<1 Year' },
  { label: '1y', value: '1 Year' },
  { label: '2y', value: '2 Years' },
  { label: '3y', value: '3 Years' },
  { label: '4y', value: '4 Years' },
  { label: '5yPlus', value: '5+ Years' },
];

const buildInitialValues = (reference) =>
  reference
    ? {
        name: reference.name,
        email: reference.email,
        phone_number: convertPhoneNumberStringToObject(reference.phone_number),
        phone_type: reference.phone_type,
        reference_type: reference.reference_type,
        relationship: reference.relationship,
        duration: reference.duration,
        reference_text: reference.reference_text,
        agent_notes: reference.agent_notes,
      }
    : {};

const ReferenceDrawer = ({ reference, isVisible, onClose }) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const updateReferenceMutation = useMutation({
    mutationFn: (data) =>
      apiPutRequest(`/landlords/application_management/references/${reference.id}`, {
        ...data,
        phone_number: convertPhoneNumberObjectToString(data.phone_number),
      }),
    onSuccess() {
      message.success(translate('success'));
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      onClose();
    },
    onError() {
      message.error(translate('error'));
    },
  });

  return (
    <Drawer
      title={translate('title')}
      visible={isVisible}
      closable
      destroyOnClose
      onClose={onClose}
    >
      <DrawerContent>
        <Form
          layout="vertical"
          form={form}
          onFinish={(values) => {
            updateReferenceMutation.mutate(values);
          }}
          scrollToFirstError
          initialValues={buildInitialValues(reference)}
        >
          <StyledSpace direction="vertical" size="middle">
            <Form.Item label={translate('name')} name="name" rules={[requiredField]}>
              <Input />
            </Form.Item>

            <Form.Item
              label={getTranslatedString('email')}
              name="email"
              rules={[requiredField, { type: 'email', message: 'Invalid email' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item label={translate('phoneNumber')} name="phone_number" rules={[requiredField]}>
              <InputPhone />
            </Form.Item>

            <Form.Item label={translate('phoneType')} name="phone_type" rules={[requiredField]}>
              <Select>
                {PHONE_TYPE_OPTIONS.map((option) => (
                  <Select.Option value={option.value} key={option.value}>
                    {translate(option.label)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={translate('referenceType')}
              name="reference_type"
              rules={[requiredField]}
            >
              <Select>
                {REFERENCE_TYPE_OPTIONS.map((option) => (
                  <Select.Option value={option.value} key={option.value}>
                    {translate(option.label)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={translate('relationshipToApplicant')}
              name="relationship"
              rules={[requiredField]}
            >
              <Select>
                {RELATIONSHIP_OPTIONS.map((option) => (
                  <Select.Option value={option.value} key={option.value}>
                    {translate(option.label)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={translate('applicantRelationshipDuration')}
              name="duration"
              rules={[requiredField]}
            >
              <Select>
                {DURATION_OPTIONS.map((option) => (
                  <Select.Option value={option.value} key={option.value}>
                    {getTranslatedString(
                      `tenantPortal.application.personalReferenceArray.relationship.length.${option.label}`
                    )}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={translate('characterReference')} name="reference_text">
              <Input.TextArea rows={4} />
            </Form.Item>

            <Form.Item label={translate('agentNotes')} name="agent_notes">
              <Input.TextArea rows={4} />
            </Form.Item>

            <Divider />

            <Space>
              <Button type="primary" htmlType="submit">
                {getTranslatedString('submit')}
              </Button>
              <Button htmlType="button" onClick={onClose}>
                {getTranslatedString('cancel')}
              </Button>
            </Space>
          </StyledSpace>
        </Form>
      </DrawerContent>
    </Drawer>
  );
};

export default ReferenceDrawer;
